// src/components/ui/TaskTableView.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import BadgeDropdown from './BadgeDropdown';
import CustomDropdown from './CustomDropdown';

// --- Filter Options ---
const domainOptions = ['Software', 'Electronics', 'Mechanical', 'General'];
const priorityOptions = ['High', 'Medium', 'Low'];
const statusOptions = ['In Progress', 'Not Decided', 'Pending', 'Completed'];

// --- Badge Styling Helper ---
const getBadgeClass = (type, value) => {
  if (type === 'priority') {
    switch (value) {
      case 'High': return 'bg-red-500/20 text-red-400 border border-red-500/30';
      case 'Medium': return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30';
      case 'Low': return 'bg-green-500/20 text-green-400 border border-green-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
    }
  }
  if (type === 'domain') {
    switch (value) {
      case 'Software': return 'bg-purple-500/20 text-purple-400 border border-purple-500/30';
      case 'Electronics': return 'bg-blue-500/20 text-blue-400 border border-blue-500/30';
      case 'Mechanical': return 'bg-orange-500/20 text-orange-400 border border-orange-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
    }
  }
  if (type === 'status') {
    switch (value) {
      case 'In Progress': return 'bg-orange-500/20 text-orange-400 border border-orange-500/30';
      case 'Completed': return 'bg-green-500/20 text-green-400 border border-green-500/30';
      case 'Pending': return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30';
      case 'Not Decided': return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
    }
  }
  return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
};

const TaskTableView = () => {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- States for filters ---
  const [filterDomain, setFilterDomain] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterStatus, setFilterStatus] = useState(''); // <-- NEW FILTER STATE

  // --- NEW: State for Click-to-Edit ---
  // Will store { rowId: '...', column: 'status' | 'assignee' | 'deadline' }
  const [editingCell, setEditingCell] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      let query = supabase
        .from('problems')
        .select('id, title, status, domain, priority, deadline_date, assigned_user:users(id, email)');

      if (filterDomain) query = query.eq('domain', filterDomain);
      if (filterPriority) query = query.eq('priority', filterPriority);
      if (filterAssignee) query = query.eq('assigned_to', filterAssignee);
      if (filterStatus) query = query.eq('status', filterStatus); // <-- NEW FILTER

      query = query.order('created_at', { ascending: false });

      const { data: taskData, error: taskError } = await query;

      if (taskError) {
        console.error('Error fetching tasks:', taskError);
        setError(taskError.message);
      } else {
        setTasks(taskData);
      }

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email');

      if (userError) {
        console.error('Error fetching users:', userError);
      } else {
        setUsers(userData);
      }

      setLoading(false);
    };

    fetchData();
  }, [filterDomain, filterPriority, filterAssignee, filterStatus]); // <-- Added filter

  // --- UPDATED HANDLERS ---
  // They now update local state AND close the editor
  const handleUpdateStatus = async (problemId, newStatus) => {
    setEditingCell(null); // Close editor
    const { error } = await supabase
      .from('problems')
      .update({ status: newStatus })
      .eq('id', problemId);

    if (error) {
      console.error('Error:', error);
      alert('Failed to update status.');
    } else {
      setTasks(prev => prev.map(t => t.id === problemId ? { ...t, status: newStatus } : t));
    }
  };

  const handleAssignUser = async (problemId, newUserId) => {
    setEditingCell(null); // Close editor
    const userIdToSet = newUserId === "" ? null : newUserId;

    const { error } = await supabase
      .from('problems')
      .update({ assigned_to: userIdToSet })
      .eq('id', problemId);

    if (error) {
      console.error('Error:', error);
      alert('Failed to assign user.');
    } else {
      setTasks(prev => prev.map(t => {
        if (t.id === problemId) {
          const assignedUser = users.find(u => u.id === userIdToSet);
          return { ...t, assigned_user: assignedUser ? { id: assignedUser.id, email: assignedUser.email } : null };
        }
        return t;
      }));
    }
  };

  const handleSetDeadline = async (problemId, newDate) => {
    setEditingCell(null); // Close editor
    const { error } = await supabase
      .from('problems')
      .update({ deadline_date: newDate })
      .eq('id', problemId);

    if (error) {
      console.error('Error:', error);
      alert('Failed to set deadline.');
    } else {
      setTasks(prev => prev.map(t => t.id === problemId ? { ...t, deadline_date: newDate } : t));
    }
  };

  const handleTitleClick = (problemId) => {
    navigate('/tasks', { state: { openProblemId: problemId } });
  };

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-500">Error: {error}</div>;

  // --- Helper to render the styled badge/text ---
  const renderCellContent = (task, column) => {
    if (column === 'status') {
      return (
        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${getBadgeClass('status', task.status)}`}>
          {task.status || 'N/A'}
        </span>
      );
    }
    if (column === 'assignee') {
      return (
        <span className="text-gray-200">
          {task.assigned_user ? task.assigned_user.email : <span className="text-gray-400">Unassigned</span>}
        </span>
      );
    }
    if (column === 'deadline') {
      return (
        <span className="text-gray-200">
          {task.deadline_date ? format(new Date(task.deadline_date), 'MMM dd, yyyy') : <span className="text-gray-400">No deadline</span>}
        </span>
      );
    }
  };

  // --- Main component render ---
  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6 text-gray-100">Assignments</h1>

      {/* --- Filter Bar --- */}
      <div className="flex flex-wrap gap-4 mb-6">
        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Status
          </label>
          <BadgeDropdown
            value={filterStatus}
            onChange={setFilterStatus}
            type="status"
            placeholder="All Statuses"
            options={statusOptions.map(opt => ({ value: opt, label: opt }))}
            className="min-w-[140px]"
          />
        </div>

        {/* Domain Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Domain
          </label>
          <BadgeDropdown
            value={filterDomain}
            onChange={setFilterDomain}
            type="domain"
            placeholder="All Domains"
            options={domainOptions.map(opt => ({ value: opt, label: opt }))}
            className="min-w-[140px]"
          />
        </div>

        {/* Priority Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Priority
          </label>
          <BadgeDropdown
            value={filterPriority}
            onChange={setFilterPriority}
            type="priority"
            placeholder="All Priorities"
            options={priorityOptions.map(opt => ({ value: opt, label: opt }))}
            className="min-w-[120px]"
          />
        </div>

        {/* Assignee Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Assigned To
          </label>
          <CustomDropdown
            value={filterAssignee}
            onChange={setFilterAssignee}
            placeholder="All Users"
            options={[
              { value: '', label: 'All Users' },
              ...users.map(user => ({ value: user.id, label: user.email }))
            ]}
            className="min-w-[160px]"
          />
        </div>
      </div>

      {/* --- Table --- */}
      <div className="glass-card overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-600">
          <thead className="bg-gray-800/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Problem Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Domain
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Priority
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Assigned To
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Deadline
              </th>
            </tr>
          </thead>
          <tbody className="bg-gray-900/20 divide-y divide-gray-600">
            {tasks.length === 0 && (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-gray-400">
                  No tasks found.
                </td>
              </tr>
            )}
            {tasks.map((task) => (
              <tr key={task.id} className="hover:bg-gray-800/30 transition-colors">
                {/* Title Cell (Link) */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div
                    className="text-sm font-medium text-indigo-400 hover:text-indigo-300 hover:underline cursor-pointer"
                    onClick={() => handleTitleClick(task.id)}
                  >
                    {task.title}
                  </div>
                </td>

                {/* Status Cell (Click-to-Edit) */}
                <td 
                  className="px-6 py-4 whitespace-nowrap cursor-pointer hover:bg-gray-800/50" 
                  onClick={() => setEditingCell({ rowId: task.id, column: 'status' })}
                >
                  {editingCell?.rowId === task.id && editingCell?.column === 'status' ? (
                    <div className="relative z-10">
                      <BadgeDropdown
                        value={task.status}
                        onChange={(value) => handleUpdateStatus(task.id, value)}
                        type="status"
                        options={statusOptions.map(opt => ({ value: opt, label: opt }))}
                        className="min-w-[140px]"
                      />
                    </div>
                  ) : (
                    renderCellContent(task, 'status')
                  )}
                </td>

                {/* Domain Cell (Static) */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${getBadgeClass('domain', task.domain)}`}>
                    {task.domain || 'N/A'}
                  </span>
                </td>

                {/* Priority Cell (Static) */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${getBadgeClass('priority', task.priority)}`}>
                    {task.priority || 'N/A'}
                  </span>
                </td>

                {/* Assignee Cell (Click-to-Edit) */}
                <td 
                  className="px-6 py-4 whitespace-nowrap cursor-pointer hover:bg-gray-800/50" 
                  onClick={() => setEditingCell({ rowId: task.id, column: 'assignee' })}
                >
                  {editingCell?.rowId === task.id && editingCell?.column === 'assignee' ? (
                    <div className="relative z-10">
                      <CustomDropdown
                        value={task.assigned_user ? task.assigned_user.id : ''}
                        onChange={(value) => handleAssignUser(task.id, value)}
                        options={[
                          { value: '', label: 'Unassigned' },
                          ...users.map(user => ({ value: user.id, label: user.email }))
                        ]}
                        className="min-w-[160px]"
                      />
                    </div>
                  ) : (
                    renderCellContent(task, 'assignee')
                  )}
                </td>

                {/* Deadline Cell (Click-to-Edit) */}
                <td 
                  className="px-6 py-4 whitespace-nowrap cursor-pointer hover:bg-gray-800/50" 
                  onClick={() => setEditingCell({ rowId: task.id, column: 'deadline' })}
                >
                  {editingCell?.rowId === task.id && editingCell?.column === 'deadline' ? (
                    <input
                      type="date"
                      autoFocus
                      value={task.deadline_date || ''}
                      onBlur={() => setEditingCell(null)}
                      onChange={(e) => handleSetDeadline(task.id, e.target.value)}
                      className="glass-input text-sm"
                    />
                  ) : (
                    renderCellContent(task, 'deadline')
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TaskTableView;